/**
 * The asset-class filter, shared by the watchlist and the market-signal tab.
 *
 * 143 symbols across four asset classes stopped being one list a while ago.
 * The first cut is always "which market", so it lives here once rather than
 * being reimplemented per panel -- two copies would drift, and the industry
 * chips already leaked across asset classes once when the filter logic was
 * local to the watchlist.
 */

import type { AssetClass, Signal, Symbol } from '@/api/types'

export type AssetTab = AssetClass | 'all'

export const ASSET_ORDER: AssetClass[] = ['crypto', 'forex', 'gold', 'stock_vn']

export const ASSET_LABELS: Record<AssetClass, string> = {
  crypto: 'Crypto',
  forex: 'Forex',
  gold: 'Vàng',
  stock_vn: 'CK VN',
}

/** Majors, in the sense the FX desk means: everything quoted against USD. */
export const FX_MAJORS = new Set([
  'EURUSD', 'GBPUSD', 'USDJPY', 'USDCHF', 'USDCAD', 'AUDUSD', 'NZDUSD',
])

/**
 * Upper-case and strip diacritics, so "vang" finds "Vàng".
 *
 * Typing Vietnamese diacritics to search is exactly the friction this is
 * meant to remove, and a reader who types "vang" should not be told there
 * is nothing there.
 */
export function normalize(text: string): string {
  return text
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/đ/g, 'd')
    .replace(/Đ/g, 'D')
    .toUpperCase()
}

/**
 * Everything a symbol can be found by: its ticker, its display name, and
 * the name of its asset class -- so "vàng" and "gold" both reach XAUUSD
 * even though only one of them appears in its display name.
 */
export function symbolHaystack(symbol: Pick<Symbol, 'ticker' | 'display_name' | 'asset_class'>): string {
  return normalize(
    [symbol.ticker, symbol.display_name ?? '', ASSET_LABELS[symbol.asset_class] ?? ''].join(' '),
  )
}

export function matchesQuery(
  symbol: Pick<Symbol, 'ticker' | 'display_name' | 'asset_class'>,
  needle: string,
): boolean {
  if (!needle) return true
  return symbolHaystack(symbol).includes(needle)
}

export interface SymbolFilters {
  assetTab: AssetTab
  /** Vietnamese-equity industry code, or null. Ignored for every other class. */
  industry: string | null
  /** Already normalized by `normalize`. */
  needle: string
}

/**
 * The one predicate both panels filter by.
 *
 * It is a function rather than a closure inside a component because the
 * industry filter leaked across asset classes once already: "Tài chính"
 * listed BTCUSDT and EURUSD, because the industry test passed straight
 * through for rows that have no industry at all. Written once and tested,
 * it cannot drift between the watchlist and the signal tab.
 */
export function matchesFilters(
  symbol: Pick<Symbol, 'ticker' | 'display_name' | 'asset_class' | 'industry_code'>,
  { assetTab, industry, needle }: SymbolFilters,
): boolean {
  if (assetTab !== 'all' && symbol.asset_class !== assetTab) return false
  if (industry) {
    // An industry is a Vietnamese-equity concept. Anything else drops out
    // entirely rather than passing through untested.
    if (symbol.asset_class !== 'stock_vn') return false
    if (symbol.industry_code !== industry) return false
  }
  return matchesQuery(symbol, needle)
}

/** Counts per tab, derived from the data so a universe refresh moves them. */
export function assetCounts(
  symbols: Pick<Symbol, 'asset_class'>[],
): Record<AssetTab, number> {
  const counts = { all: 0, crypto: 0, forex: 0, gold: 0, stock_vn: 0 } as Record<AssetTab, number>
  for (const symbol of symbols) {
    counts.all += 1
    counts[symbol.asset_class] = (counts[symbol.asset_class] ?? 0) + 1
  }
  return counts
}

export interface TickerSignal {
  side: Signal['side']
  confidence: number
  count: number
}

/**
 * Best open signal per ticker, for ordering and for the dot in the list.
 *
 * "Best" is the highest confidence: when a symbol has a scalp and a swing
 * idea at once, the list can only show one, and the stronger one is the
 * reason the row is worth looking at. The count is kept so the row can
 * still say there is more than one behind it.
 *
 * Built from the market-signal query both panels already run, so this costs
 * no request -- the watchlist and the signal tab share one React Query
 * cache entry.
 */
export function topSignalByTicker(signals: Signal[] | undefined): Map<string, TickerSignal> {
  const best = new Map<string, TickerSignal>()
  for (const signal of signals ?? []) {
    if (signal.status !== 'active') continue
    const current = best.get(signal.ticker)
    if (current === undefined) {
      best.set(signal.ticker, {
        side: signal.side, confidence: signal.confidence, count: 1,
      })
      continue
    }
    current.count += 1
    if (signal.confidence > current.confidence) {
      current.side = signal.side
      current.confidence = signal.confidence
    }
  }
  return best
}

/**
 * Signals first, strongest first; everything else keeps the order it came in.
 *
 * A stable partition rather than a full re-sort: the second group already
 * has an order that means something -- turnover for crypto, ticker for the
 * rest -- and re-sorting it on a field it does not have would scramble it.
 */
export function signalsFirst<T>(
  rows: T[],
  tickerOf: (row: T) => string,
  signals: Map<string, TickerSignal>,
): T[] {
  const withSignal: T[] = []
  const without: T[] = []
  for (const row of rows) {
    (signals.has(tickerOf(row)) ? withSignal : without).push(row)
  }
  withSignal.sort(
    (a, b) =>
      (signals.get(tickerOf(b))?.confidence ?? 0) -
      (signals.get(tickerOf(a))?.confidence ?? 0),
  )
  return [...withSignal, ...without]
}

/**
 * The dot beside a ticker: green long, red short -- the same two colours the
 * prices already use, so nothing new has to be learned to read it.
 */
export function SignalDot({ signal }: { signal: TickerSignal | undefined }) {
  if (!signal) return null
  const long = signal.side === 'long'
  return (
    <span
      title={
        `${signal.count} tín hiệu đang mở · ${long ? 'MUA' : 'BÁN'} · ` +
        `tin cậy ${signal.confidence.toFixed(0)}`
      }
      className={`ml-1.5 inline-flex items-center gap-0.5 rounded px-1 text-2xs ${
        long ? 'bg-up/20 text-up' : 'bg-down/20 text-down'
      }`}
    >
      <span aria-hidden>{long ? '▲' : '▼'}</span>
      {signal.count > 1 && <span>{signal.count}</span>}
    </span>
  )
}

export function AssetTabs({
  value, counts, onChange,
}: {
  value: AssetTab
  counts: Record<AssetTab, number>
  onChange: (tab: AssetTab) => void
}) {
  const tabs: AssetTab[] = ['all', ...ASSET_ORDER]
  return (
    <div className="flex flex-wrap gap-1">
      {tabs.map((tab) => {
        const count = counts[tab] ?? 0
        const label = tab === 'all' ? 'Tất cả' : ASSET_LABELS[tab]
        return (
          <button
            key={tab}
            onClick={() => onChange(tab)}
            // A tab with nothing behind it is shown, not hidden: "Vàng (0)"
            // says the feed is empty, while a missing tab looks like the
            // asset class was never configured.
            className={`rounded px-1.5 py-0.5 text-2xs transition-colors ${
              value === tab
                ? 'bg-accent/25 text-ink'
                : 'bg-panel text-ink-dim hover:text-ink'
            }`}
          >
            {label} <span className="opacity-60">{count}</span>
          </button>
        )
      })}
    </div>
  )
}

/**
 * Highlight the matched run inside a ticker.
 *
 * The point of auto-switching tabs on a search is that the reader can see
 * why they were moved; an unmarked row in a different tab looks like the UI
 * lost their place.
 */
export function Highlight({ text, needle }: { text: string; needle: string }) {
  if (!needle) return <>{text}</>
  const at = normalize(text).indexOf(needle)
  if (at < 0) return <>{text}</>
  return (
    <>
      {text.slice(0, at)}
      <mark className="bg-accent/40 text-ink">{text.slice(at, at + needle.length)}</mark>
      {text.slice(at + needle.length)}
    </>
  )
}
